import { PrismicRichText } from '@prismicio/react'
import ExpandDisclosureIcon from 'src/images/svgs/icons/ExpandDisclosureIcon.svg'
import ContractDisclosureIcon from 'src/images/svgs/icons/ContractDisclosureIcon.svg'
import React, {
  FC,
  SyntheticEvent,
  useMemo,
  useState,
  type ReactElement,
} from 'react'
import { PrismicStructuredTextType } from 'src/typings/generated/graphql'
import CustomLink from 'src/utils/customLink'
import * as Styles from './richTextDisclosure.module.scss'

/**
 * Props for the `RichTextDisclosure` component.
 */
export type RichTextDisclosureProps = {
  /**
   * The text to display in the summary of the disclosure. This text will be visible when the disclosure is collapsed.
   */
  summaryText: string
  /**
   * The rich text content to display inside the disclosure. This content will only be visible when the disclosure is
   * expanded.
   */
  disclosureRichText: PrismicStructuredTextType
  /**
   * Whether the disclosure is currently expanded. If provided, the component will be in controlled mode and will not * manage its own state.
   */
  isExpanded?: boolean
  /**
   * The name of the group that this disclosure belongs to. This is used to ensure that only one disclosure in the
   * group can be expanded at a time. If not provided, the disclosure will not be part of any group.
   */
  groupName?: string
  /**
   * Callback fired when the disclosure is toggled. Receives the new expanded state. Use together with `isExpanded`
   * for controlled mode, or on its own to react to uncontrolled toggles.
   */
  onToggle?: (isExpanded: boolean) => void
}

/**
 * A disclosure component that displays a summary and rich text content.
 *
 * Can be used in controlled mode by providing `isExpanded` and `onToggle`, or in uncontrolled mode by omitting them.
 */
export const RichTextDisclosure: FC<RichTextDisclosureProps> = ({
  summaryText,
  disclosureRichText,
  isExpanded,
  groupName,
  onToggle,
}) => {
  const isControlled = useMemo(() => isExpanded !== undefined, [isExpanded])

  const [uncontrolledIsExpanded, setUncontrolledIsExpanded] =
    useState(isExpanded)

  const currentlyExpanded = useMemo(
    () => (isControlled ? isExpanded : uncontrolledIsExpanded),
    [isControlled, isExpanded, uncontrolledIsExpanded]
  )

  /**
   * The icon to display in the summary of the disclosure. The transition between the two states is animated using CSS
   * transitions.
   */
  const summaryIcon = useMemo<ReactElement>(() => {
    return (
      <>
        <ContractDisclosureIcon
          className={`${Styles.contractIcon} ${
            currentlyExpanded ? Styles.iconVisible : ''
          }`}
        />
        <ExpandDisclosureIcon
          className={`${Styles.expandIcon} ${
            currentlyExpanded ? '' : Styles.iconVisible
          }`}
        />
      </>
    )
  }, [currentlyExpanded])

  const handleToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    setUncontrolledIsExpanded(event.currentTarget.open)
    onToggle?.(event.currentTarget.open)
  }

  return (
    <details
      className={Styles.richTextDisclosure}
      open={currentlyExpanded}
      name={groupName}
      onToggle={handleToggle}
    >
      <summary className={Styles.summary}>
        <span className={Styles.summaryText}>{summaryText}</span>
        <span className={Styles.summaryIconWrapper}>{summaryIcon}</span>
      </summary>
      <div className={Styles.disclosureContent}>
        <PrismicRichText
          field={disclosureRichText.richText}
          components={{
            hyperlink: ({ node, children }: any) =>
              CustomLink({ link: node.data, children }),
          }}
        />
      </div>
    </details>
  )
}
